import type { Locale } from '@/i18n/domainConfig';

/**
 * Narrows next-intl app-wide, so `useLocale()`, `getLocale()`,
 * `setRequestLocale()`, `getPathname({locale})`, `<Link locale>` and
 * `redirect({locale})` all speak `Locale` instead of `string`.
 */
declare module 'next-intl' {
  interface AppConfig {
    Locale: Locale;
  }
}

export type { Locale };

/*
 * Wire formats. Every `Locale` contains `-` and every wire locale contains `_`,
 * so the unions below are disjoint from `Locale` and assignability alone keeps
 * the formats from being mixed up. Pass `Locale` everywhere; convert here, at
 * the request boundary, and nowhere else.
 */

type Underscored<T extends string> = T extends `${infer L}-${infer C}` ? `${L}_${C}` : T;

/** Underscored ISO locale, e.g. `en_US`. For outbound requests only. */
export type ApiLocale = Underscored<Locale>;

/**
 * The platform's own locale format. Mostly identical to {@link ApiLocale}, but
 * five locales carry a pseudo-country in the language slot (`en-US` → `us_US`)
 * or swap language for country (`sv-SE` → `se_SE`).
 *
 * `satisfies` keeps this exhaustive: adding a locale to `allLocales` without a
 * mapping here is a compile error rather than a silently malformed request.
 */
const sprdLocaleMapping = {
  'de-DE': 'de_DE',
  'de-AT': 'de_AT',
  'de-CH': 'de_CH',
  'en-GB': 'en_GB',
  'en-IE': 'en_IE',
  'fr-FR': 'fr_FR',
  'fr-BE': 'fr_BE',
  'fr-CH': 'fr_CH',
  'fr-CA': 'fr_CA',
  'es-ES': 'es_ES',
  'it-IT': 'it_IT',
  'it-CH': 'it_CH',
  'nl-NL': 'nl_NL',
  'nl-BE': 'nl_BE',
  'no-NO': 'no_NO',
  'pl-PL': 'pl_PL',
  'fi-FI': 'fi_FI',
  'en-US': 'us_US',
  'en-CA': 'us_CA',
  'en-AU': 'us_AU',
  'sv-SE': 'se_SE',
  'da-DK': 'dk_DK',
} as const satisfies Record<Locale, string>;

/** Platform locale, e.g. `us_US`. For outbound requests only. */
export type SprdLocale = (typeof sprdLocaleMapping)[Locale];

type LanguageOf<T extends string> = T extends `${infer L}-${string}` ? L : never;
type CountryOf<T extends string> = T extends `${string}-${infer C}` ? C : never;

/** Two-letter language subtag, e.g. `en`. */
export type Language = LanguageOf<Locale>;
/** ISO 3166-1 alpha-2 country code, e.g. `US`. */
export type CountryCode = CountryOf<Locale>;

/** `en-US` → `en_US`. */
export const getApiLocale = (locale: Locale) => locale.replace('-', '_') as ApiLocale;

/** `en-US` → `us_US`. */
export const getSprdLocale = (locale: Locale): SprdLocale => sprdLocaleMapping[locale];

/** `en-US` → `en`. Derived from the canonical locale — never from a wire locale. */
export const getLanguage = (locale: Locale) => locale.split('-')[0] as Language;

/** `en-US` → `US`. Derived from the canonical locale — never from a wire locale. */
export const getCountry = (locale: Locale) => locale.split('-')[1] as CountryCode;
